import { useContext } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { usePublicUserLoginMutation } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { spacing } from "../../../stitches.config";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import {
  ContentSection,
  HeaderSection,
  UpdatedContainer,
} from "../../Common/__styles__/Modal";
import { ButtonLink } from "../../Common/ButtonLink";
import { Title } from "../../Common/Typography";
import { LoginForm } from "../__styles__/LoginPage";
import {
  ProvideCredentialsForm,
  ProvideCredentialsFormProps,
} from "../ProvideCredentialsForm";
import { PUBLIC_USER_AUTH_MODALS } from "./PublicUserAuthModal";

export type ProvideCredentialsFormStructure = {
  email?: Maybe<string>;
  password?: Maybe<string>;
};

export const PublicUserLoginModalContent = ({
  setActiveModal,
  hideModal,
}: {
  setActiveModal: (modal: PUBLIC_USER_AUTH_MODALS) => void;
  hideModal: () => void;
}) => {
  const { t } = useTranslation();
  const useFormProps = useForm<ProvideCredentialsFormStructure>();
  const { refetchAuthorizedEntity } = useContext(AuthContext);
  const { addErrorToast } = useStatusToasts();

  const [login, { data }] = usePublicUserLoginMutation({
    onCompleted: async data => {
      if (data.publicUserLogin.ok) {
        track("Public user logged in");
        await refetchAuthorizedEntity();
        hideModal();
      }
    },
    onError: () => {
      addErrorToast(t("sign-in-unexpected-network-error"));
    },
  });
  const error = data?.publicUserLogin.error ?? null;

  const handleLogin = async ({
    email,
    password,
  }: Parameters<ProvideCredentialsFormProps["onLogin"]>[0]) => {
    await login({
      variables: { email, password },
    });
  };

  return (
    <FormProvider {...useFormProps}>
      <UpdatedContainer size="small">
        <HeaderSection>
          <Title size="large" type="regular">
            {t("sign-in-to-your-account")}
          </Title>
        </HeaderSection>
        <ContentSection>
          <LoginForm>
            <ProvideCredentialsForm
              error={error}
              onLogin={handleLogin}
              onClickPasswordReset={() =>
                setActiveModal(PUBLIC_USER_AUTH_MODALS.PASSWORD_RESET)
              }
            />
            <div
              style={{
                marginTop: spacing.m.toString(),
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <ButtonLink
                styleVariant="primary"
                size="medium"
                onClick={() =>
                  setActiveModal(PUBLIC_USER_AUTH_MODALS.CREATE_ACCOUNT)
                }
              >
                {t("create-account")}
              </ButtonLink>
            </div>
          </LoginForm>
        </ContentSection>
      </UpdatedContainer>
    </FormProvider>
  );
};
